<template>
  <div class="affiliate-statistics-and-payments-statistics-tab-wrapper">
    <div class="affiliate-statistics-and-payments-statistics-tab">
      <custom-select
        :items-list="subAccountsList"
        :selected-items="selectedSubAccounts"
        item-text="source"
        :loading="false"
        class="affiliate-statistics-statistics-tab-links-select"
        @handleSelected="handleSubAccountsSelect"
        @loadMoreItems="loadMoreAffiliateLinks"
      >
        <template #prepend-inner>
          <div>
            {{ subAccountsSelectionText }}
          </div>
        </template>
      </custom-select>
      <date-range
        :selected-range="selectedStatisticsDateRange"
        @handleSelected="changeSelectedStatisticsDateRange"
      />
    </div>

    <div
      v-if="!areSelectedAllSubAccounts && selectedSubAccounts.length"
      class="affiliate-statistics-tab-chips-wrapper"
    >
      <v-chip
        v-for="(item, index) in selectedSubAccounts"
        :key="index"
        close
        label
        outlined
        class="affiliate-statistics-tab-chips"
        @click:close="handleSubAccountsSelect(item)"
      >
        {{ item.source }}
      </v-chip>
    </div>

    <v-row class="mt-3">
      <v-col class="affiliate-statistics-tab-users">
        {{ $t("ref.registeredUsers") }}
      </v-col>
      <v-col
        cols="8"
        class="affiliate-statistics-tab-users-value"
      >
        {{ statisticsUsers }}
      </v-col>

      <div class="custom-divider" />

      <!-- БАЛАНС -->
      <v-col class="affiliate-statistics-tab-balance">
        {{ $t("affiliateProgram.balance") }}
      </v-col>
      <v-col
        cols="8"
        class="affiliate-statistics-tab-balance-value"
      >
        {{ `$ ${statisticsBalance}` }}
      </v-col>

      <div class="custom-divider" />

      <v-col cols="12" class="pt-0 mt-8">
        <div class="affiliate-statistics-tab-table-title">
          {{ $t('affiliateProgram.links.transactions') }}
        </div>
        <main-table />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import MainTable from '@/components/affiliate/AffiliateStatisticsMainTable.vue'
import DateRange from '@/components/dateRanges/DateRange.vue'
import CustomSelect from '@/components/CustomSelect.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name:'AffiliateStatisticsTab',
  components: {
    CustomSelect,
    DateRange,
    MainTable
  },
  computed: {
    ...mapGetters({
      profile: 'main/profile',
      affiliateLinksList: 'affiliate/linksForStatisticsFilterItems',
      affiliateLinksPagination: 'affiliate/linksPaginationForStatisticsFilter',
      statisticsFilters: 'affiliate/statisticsFilters',
      statisticsBalance: 'affiliate/statisticsBalance',
      statisticsUsers: 'affiliate/statisticsUsers',
    }),
    selectedStatisticsDateRange() {
      return this.statisticsFilters.statisticsDate
    },
    areSelectedAllSubAccounts() {
      return this.selectedSubAccounts.length === this.subAccountsList.length
    },
    selectedSubAccounts() {
      return this.statisticsFilters.selectedSubAccounts
    },
    subAccountsList () {
      return [
        {
          id: '',
          isMainLink: true,
          source: `${location.origin}/a/${this.profile.id}/`
        },
        ...this.affiliateLinksList
      ]
    },
    subAccountsSelectionText () {
      if (this.selectedSubAccounts.length === 1) {
        return this.selectedSubAccounts[0].source
      }

      if (!this.selectedSubAccounts.length) {
        return this.$t('affiliateProgram.links.nothingSelected')
      }

      if (this.areSelectedAllSubAccounts) {
        return this.$t('affiliateProgram.links.allSubAccounts')
      }

      if (this.selectedSubAccounts.length > 1) {
        return `${this.$t('affiliateProgram.links.subAccountsSelected')} ${this.selectedSubAccounts.length}`
      }

      return ''
    },
  },
  async created() {
    await this.loadLinksForStatisticsFilter()
    this.handleAllSubAccountsSelection()
  },
  methods: {
    ...mapActions({
      loadStatistics: 'affiliate/loadStatistics',
      setStatisticsFilters: 'affiliate/setStatisticsFilters',
      loadLinksForStatisticsFilter: 'affiliate/loadLinksForStatisticsFilter',
    }),
    async handleAllSubAccountsSelection() {
      await this.loadAllAffiliateLinks()

      await this.setStatisticsFilters({
        key: 'selectedSubAccounts',
        value: [
          {
            id: '',
            isMainLink: true,
            source: `${location.origin}/a/${this.profile.id}/`
          },
          ...this.affiliateLinksList
        ]
      })

      this.loadStatistics()
    },
    async loadAllAffiliateLinks( ) {
      while (this.affiliateLinksPagination.totalItemsCount > this.affiliateLinksList.length) {
        await this.loadMoreAffiliateLinks();
      }
    },
    changeSelectedStatisticsDateRange(date) {
      this.setStatisticsFilters({
        key: 'statisticsDate',
        value: date
      })

      if ((date.value === 'specificDate' && !date.dateFrom) ||
          (date.value === 'range' && (!date.dateFrom || !date.dateTo))
      ) {
        return
      }

      this.loadStatistics()
    },
    handleSubAccountsSelect(item) {
      const { value, id } = item;

      if (value === 'selectAll') {
        const allSelected = this.selectedSubAccounts.length === this.subAccountsList.length

        return this.changeSelectedSubAccounts(allSelected ? [] : this.subAccountsList)
      }

      const isSelected = this.selectedSubAccounts.find(i => i.id === id)
      const selectedItems = isSelected
        ? this.selectedSubAccounts.filter(i => i.id !== item.id)
        : [...this.selectedSubAccounts, item]

      this.changeSelectedSubAccounts(selectedItems)
    },
    changeSelectedSubAccounts(selectedItems) {
      const notFilteringChange = this.notFilteringChange(selectedItems)

      this.setStatisticsFilters({
        key: 'selectedSubAccounts',
        value: selectedItems
      })

      if (notFilteringChange) return

      this.loadStatistics()
    },
    notFilteringChange (selectedItems) {
      const total = this.affiliateLinksPagination.totalItemsCount + 1;
      const currentFiltersAll = this.selectedSubAccounts.length === total;
      const newFiltersAll = selectedItems.length === total;
      const currentFiltersEmpty = !this.selectedSubAccounts.length;
      const newFiltersEmpty = !selectedItems.length;

      return (currentFiltersAll || newFiltersAll) && (currentFiltersEmpty || newFiltersEmpty);
    },
    async loadMoreAffiliateLinks () {
      if (this.affiliateLinksPagination.currentPage === this.affiliateLinksPagination.lastPage) return

      const areAllSelected = this.selectedSubAccounts.length === this.affiliateLinksPagination.total + 1

      await this.loadLinksForStatisticsFilter()

      if (areAllSelected) {
        this.handleAllSubAccountsSelection()
      }
    }
  }
}
</script>

<style lang="scss">
.affiliate-statistics-and-payments-statistics-tab-wrapper {
  display: flex;
  flex-direction: column;
}

.affiliate-statistics-and-payments-statistics-tab {
  display: flex;
  column-gap: 8px;

  .v-input__prepend-inner {
    color: var(--v-input-foreground-typed-base);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin: auto 0;
    text-wrap: nowrap;
    overflow: hidden;
  }

  .v-input__prepend-inner div {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .affiliate-statistics-statistics-tab-links-select .v-select__slot {
    max-width: max-content;
  }

  .affiliate-statistics-statistics-tab-links-select .v-select__slot .v-select__selections {
    display: none;
  }
}

.affiliate-statistics-tab-chips-wrapper {
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.affiliate-statistics-tab-users,
.affiliate-statistics-tab-balance,
.affiliate-statistics-tab-users-value,
.affiliate-statistics-tab-balance-value {
  display: flex;
  align-items: center;
  color: var(--v-text-primary-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  padding: 8px 12px;
  height: fit-content;
}
.affiliate-statistics-tab-users-value,
.affiliate-statistics-tab-balance-value {
  justify-content: end;
}

.affiliate-statistics-tab-table-title {
  margin-bottom: 16px;
  color: var(--v-text-primary-base);
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}

.affiliate-statistics-tab-chips {
  &.v-chip.v-chip--outlined.v-chip.v-chip {
    background-color: var(--v-transparent-tag-brand-background-default-base)!important;
    border: unset;
  }
  &.v-chip:not(.v-chip--active) {
    border: none;
  }
  &.v-chip--label {
    border-radius: 6px!important;
  }
  span {
    color: var(--v-tag-brand-background-hover-base);
  }
}
</style>
